<template>
  <div class="double-slider-container">
    <!-- Slider -->
    <div class="slider" ref="slider" @mousedown="onMouseDownTrack">
      <div class="slider-track"></div>
      <div class="slider-range" :style="rangeStyle"></div>
      <div class="slider-handle handle-lower" :style="handleLowerStyle"
        @mousedown.stop="onMouseDownHandle('lower', $event)">
      </div>
      <div class="slider-handle handle-upper" :style="handleUpperStyle"
        @mousedown.stop="onMouseDownHandle('upper', $event)">
      </div>
    </div>
    <!-- Display values and dropdowns -->
    <div class="slider-info">
      <!-- <span class="min-val">Min: {{ min }}</span> -->
      <div class="input-container">
        <label for="lower-select">Lower:</label>
        <select id="lower-select" v-model.number="lowerValue">
          <option v-for="choice in lowerChoices" :key="choice" :value="choice">
            {{ choice }}
          </option>
        </select>
        <label for="upper-select">Upper:</label>
        <select id="upper-select" v-model.number="upperValue">
          <option v-for="choice in upperChoices" :key="choice" :value="choice">
            {{ choice }}
          </option>
        </select>
      </div>
      <!-- <span class="max-val">Max: {{ max }}</span> -->
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onBeforeUnmount, watch } from 'vue';

export default {
  name: 'DoubleSlider',
  props: {
    modelValue: {
      type: Array,
      default: () => [0, 100]
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    // New prop for possible choices for the dropdowns.
    choices: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const slider = ref(null);
    const dragging = reactive({
      active: false,
      which: null
    });
    const internalValue = ref([...props.modelValue]);

    // Keep internal values in sync with external changes
    watch(() => props.modelValue, (newVal) => {
      internalValue.value = [...newVal];
    });

    // Update and emit new values
    const updateValue = (newVal) => {
      internalValue.value = newVal;
      emit('update:modelValue', newVal);
    };

    // Calculate handle positions (in percent)
    const percentLower = computed(() =>
      ((internalValue.value[0] - props.min) / (props.max - props.min)) * 100
    );
    const percentUpper = computed(() =>
      ((internalValue.value[1] - props.min) / (props.max - props.min)) * 100
    );

    // Styling for handles and the range with smooth transitions
    const handleLowerStyle = computed(() => ({
      left: `${percentLower.value}%`,
      transition: dragging.active && dragging.which === 'lower' ? 'none' : 'left 0.2s ease'
    }));
    const handleUpperStyle = computed(() => ({
      left: `${percentUpper.value}%`,
      transition: dragging.active && dragging.which === 'upper' ? 'none' : 'left 0.2s ease'
    }));
    const rangeStyle = computed(() => ({
      left: `${percentLower.value}%`,
      width: `${percentUpper.value - percentLower.value}%`,
      backgroundColor: '#10b981',
      transition: dragging.active ? 'none' : 'width 0.2s ease'
    }));

    // Computed getters and setters for dropdown inputs
    const lowerValue = computed({
      get() {
        return internalValue.value[0];
      },
      set(value) {
        let newVal = parseFloat(value);
        if (isNaN(newVal)) return;
        // Clamp to min and not above the current upper value
        newVal = Math.max(props.min, Math.min(newVal, internalValue.value[1]));
        updateValue([newVal, internalValue.value[1]]);
      }
    });
    const upperValue = computed({
      get() {
        return internalValue.value[1];
      },
      set(value) {
        let newVal = parseFloat(value);
        if (isNaN(newVal)) return;
        // Clamp to max and not below the current lower value
        newVal = Math.min(props.max, Math.max(newVal, internalValue.value[0]));
        updateValue([internalValue.value[0], newVal]);
      }
    });

    // Computed arrays for the dropdown choices.
    const lowerChoices = computed(() => {
      return props.choices
        .filter(choice => choice >= props.min && choice <= internalValue.value[1])
        .sort((a, b) => a - b);
    });

    const upperChoices = computed(() => {
      return props.choices
        .filter(choice => choice <= props.max && choice >= internalValue.value[0])
        .sort((a, b) => a - b);
    });

    // Helper function: snaps the raw value to the nearest value in a given choices array.
    const getClosest = (choices, target) => {
      if (!choices || choices.length === 0) return target;
      return choices.reduce((prev, curr) =>
        Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
      );
    };

    // Mouse events for dragging
    const onMouseDownHandle = (which, event) => {
      event.preventDefault();
      dragging.active = true;
      dragging.which = which;
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseDownTrack = (event) => {
      const rect = slider.value.getBoundingClientRect();
      const clickPercent = ((event.clientX - rect.left) / rect.width) * 100;
      // Choose the closest handle
      const lowerDiff = Math.abs(clickPercent - percentLower.value);
      const upperDiff = Math.abs(clickPercent - percentUpper.value);
      dragging.which = lowerDiff < upperDiff ? 'lower' : 'upper';
      dragging.active = true;
      onMouseMove(event); // Update immediately based on click
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (event) => {
      if (!dragging.active) return;
      const rect = slider.value.getBoundingClientRect();
      let newPercent = ((event.clientX - rect.left) / rect.width) * 100;
      newPercent = Math.max(0, Math.min(100, newPercent));
      // Get raw value without snapping.
      const rawValue = props.min + ((props.max - props.min) * newPercent / 100);
      const newValues = [...internalValue.value];

      if (dragging.which === 'lower') {
        // Allow only choices between min and the current upper value.
        const validChoices = props.choices.filter(choice => choice >= props.min && choice <= internalValue.value[1]);
        // Snap the raw value to the closest valid choice.
        const snappedValue = getClosest(validChoices, rawValue);
        newValues[0] = Math.min(snappedValue, internalValue.value[1]);
      } else if (dragging.which === 'upper') {
        // Allow only choices between the current lower value and max.
        const validChoices = props.choices.filter(choice => choice <= props.max && choice >= internalValue.value[0]);
        // Snap the raw value to the closest valid choice.
        const snappedValue = getClosest(validChoices, rawValue);
        newValues[1] = Math.max(snappedValue, internalValue.value[0]);
      }
      updateValue(newValues);
    };

    const onMouseUp = () => {
      dragging.active = false;
      dragging.which = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    onBeforeUnmount(() => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    });

    return {
      slider,
      handleLowerStyle,
      handleUpperStyle,
      rangeStyle,
      onMouseDownHandle,
      onMouseDownTrack,
      lowerValue,
      upperValue,
      lowerChoices,
      upperChoices,
    };
  }
};
</script>

<style scoped>
.double-slider-container {
  max-width: 90%;
  margin: 0 var(--spacing-ml);
}

.slider {
  position: relative;
  height: 0.5rem;
  background-color: var(--colour-border-light);
  border-radius: var(--radius-sm);
  margin: var(--spacing-lg) 0;
  cursor: pointer;
}

.slider-track {
  position: absolute;
  height: 100%;
  width: 100%;
  background-color: var(--colour-border-light);
  border-radius: var(--radius-sm);
}

.slider-range {
  position: absolute;
  height: 100%;
  border-radius: var(--radius-sm);
}

.slider-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 1.25rem;
  height: 1.25rem;
  background-color: var(--colour-surface);
  border: 2px solid var(--color-secondary);
  border-radius: 50%;
  cursor: pointer;
}

.slider-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--font-size-sm);
  padding: 0 var(--spacing-md);
}

.input-container {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.input-container label {
  font-size: var(--font-size-sm);
}

.input-container select {
  width: 4.375rem;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--colour-border);
  border-radius: var(--radius-sm);
}
</style>
